/**
 * Gesture System — natural interaction for mobile navigation.
 *
 * Edge Back Swipe:
 *   Swipe from left edge of screen → browser back
 *   Threshold: 80px horizontal movement
 *   Only activates within 20px of left edge
 *
 * Integrates with NavigationManager for consistent navigation lifecycle.
 */

import { navigationManager } from './navigation-manager.js'

const EDGE_THRESHOLD = 20  // px from left edge to activate
const SWIPE_THRESHOLD = 80 // px horizontal to trigger back
const MAX_VERTICAL_DEVIATION = 60 // max vertical movement to still count as horizontal swipe

let touchStartX = 0
let touchStartY = 0
let isEdgeSwipe = false
let willTriggerBack = false

/**
 * Initialize gesture system.
 * Call once from App.svelte during mount.
 *
 * @returns {() => void} cleanup function
 */
export function initGestures() {
  if (typeof window === 'undefined') return () => {}

  function onTouchStart(e) {
    if (e.touches.length !== 1) {
      isEdgeSwipe = false
      willTriggerBack = false
      return
    }
    touchStartX = e.touches[0].clientX
    touchStartY = e.touches[0].clientY
    isEdgeSwipe = touchStartX <= EDGE_THRESHOLD && navigationManager.can_go_back()
    willTriggerBack = false
  }

  function onTouchMove(e) {
    if (!isEdgeSwipe) return
    if (e.touches.length !== 1) {
      isEdgeSwipe = false
      willTriggerBack = false
      return
    }

    const dx = e.touches[0].clientX - touchStartX
    const dy = Math.abs(e.touches[0].clientY - touchStartY)

    if (dy > MAX_VERTICAL_DEVIATION) {
      isEdgeSwipe = false
      willTriggerBack = false
      return
    }

    if (dx >= SWIPE_THRESHOLD) {
      e.preventDefault()
      willTriggerBack = true
    }
  }

  function onTouchEnd() {
    if (willTriggerBack) {
      navigationManager.back()
    }
    isEdgeSwipe = false
    willTriggerBack = false
  }

  function onTouchCancel() {
    isEdgeSwipe = false
    willTriggerBack = false
  }

  document.addEventListener('touchstart', onTouchStart, { passive: true })
  document.addEventListener('touchmove', onTouchMove, { passive: false })
  document.addEventListener('touchend', onTouchEnd, { passive: true })
  document.addEventListener('touchcancel', onTouchCancel, { passive: true })

  return () => {
    document.removeEventListener('touchstart', onTouchStart)
    document.removeEventListener('touchmove', onTouchMove)
    document.removeEventListener('touchend', onTouchEnd)
    document.removeEventListener('touchcancel', onTouchCancel)
  }
}
